import { z } from 'zod';
import { HardwareSchema } from './machines.js';
/** Kinds of binary data frames: [kind u8][streamId u32 BE][payload]. */
export const DataKind = Object.freeze({ HttpBody: 1, WsBinary: 2 });
export const MAX_TEXT_FRAME_BYTES = 1024 * 1024;
export const MAX_DATA_CHUNK_BYTES = 256 * 1024;
const StreamId = z.number().int().min(1).max(0xffffffff);
const Headers = z.array(z.tuple([z.string().max(256), z.string().max(8192)])).max(200);
const Path = z.string().min(1).max(8192).startsWith('/');
const Message = z.string().max(2000);
export const TextFrameSchema = z.union([
    z.object({ t: z.literal('hello'), agentVersion: z.string().max(32), comfyuiVersion: z.string().max(32).nullable(), comfyuiRunning: z.boolean(), hardware: HardwareSchema.optional(), network: z.array(z.unknown()).max(8).optional(),
        /** Providers this machine holds a cloud key for — names only (doc 55). */
        cloudKeys: z.array(z.string().max(32)).max(8).optional() }),
    z.object({ t: z.literal('welcome'), machineId: z.string(), name: z.string() }),
    z.object({ t: z.literal('status'), comfyuiRunning: z.boolean(), comfyuiVersion: z.string().max(32).nullable(), cloudKeys: z.array(z.string().max(32)).max(8).optional() }),
    /** A cloud machine is about to stop itself (doc 52). */
    z.object({ t: z.literal('stopping'), reason: z.enum(['idle']), idleMinutes: z.number().int().min(0).max(1440) }),
    z.object({ t: z.literal('http.req'), id: StreamId, method: z.string().max(16), path: Path, headers: Headers }),
    z.object({ t: z.literal('http.res'), id: StreamId, status: z.number().int().min(100).max(599), headers: Headers }),
    z.object({ t: z.literal('http.end'), id: StreamId }),
    z.object({ t: z.literal('http.error'), id: StreamId, message: Message }),
    z.object({ t: z.literal('ws.open'), id: StreamId, path: Path, headers: Headers }),
    z.object({ t: z.literal('ws.opened'), id: StreamId }),
    z.object({ t: z.literal('ws.error'), id: StreamId, message: Message }),
    z.object({ t: z.literal('ws.text'), id: StreamId, data: z.string() }),
    z.object({ t: z.literal('ws.close'), id: StreamId, code: z.number().int().min(1000).max(4999).optional(), reason: z.string().max(123).optional() }),
    z.object({ t: z.literal('cmd'), id: StreamId, name: z.string().max(64), args: z.record(z.string(), z.unknown()) }),
    z.object({ t: z.literal('cmd.progress'), id: StreamId, data: z.unknown() }),
    z.object({ t: z.literal('cmd.result'), id: StreamId, ok: z.literal(true), result: z.unknown() }),
    z.object({ t: z.literal('cmd.result'), id: StreamId, ok: z.literal(false), error: Message }),
    z.object({ t: z.literal('cancel'), id: StreamId }),
]);
/** Parses one text frame; undefined for anything malformed (the caller logs and ignores it). */
export function parseTextFrame(text) {
    if (text.length > MAX_TEXT_FRAME_BYTES)
        return undefined;
    let data;
    try {
        data = JSON.parse(text);
    }
    catch {
        return undefined;
    }
    const parsed = TextFrameSchema.safeParse(data);
    return parsed.success ? parsed.data : undefined;
}
export function encodeData(kind, streamId, payload) {
    const frame = new Uint8Array(5 + payload.byteLength);
    const view = new DataView(frame.buffer);
    view.setUint8(0, kind);
    view.setUint32(1, streamId, false);
    frame.set(payload, 5);
    return frame;
}
/** Decodes a binary frame; undefined for a short frame or an unknown kind. */
export function decodeData(frame) {
    if (frame.byteLength < 5)
        return undefined;
    const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
    const kind = view.getUint8(0);
    if (kind !== DataKind.HttpBody && kind !== DataKind.WsBinary)
        return undefined;
    const streamId = view.getUint32(1, false);
    if (streamId === 0)
        return undefined;
    return { kind, streamId, payload: frame.subarray(5) };
}
//# sourceMappingURL=tunnel.js.map